import { PollAnswer } from './Answer';
import { Answer } from '../Poll.model';
import { useDebounce } from '../../../../../_helpers/hooks/useDebounce';
import { usePercentages } from '../helpers/usePercentages';
import { joinString } from '../../../../../_helpers/join-string';
import { type KeyboardEvent, useRef } from 'react';

import './PollAnswers.scss';
import { getKeys } from '../../../../../_helpers/keyboard.ts';
import {
	getNextFocusableElement,
	getPrevFocusableElement,
} from '../../../../../_helpers/dom.ts';

type PollAnswersProps = {
	answers: Answer[];
	answered: string;
	showResults: boolean;
	votes: number;
	fullWidthAnimation: boolean;
	handleAnswered: (answerIndex: number) => Promise<void> | void;
	questionElementId: string;
};

export const PollAnswers: React.FC<PollAnswersProps> = ({
	answers,
	answered,
	showResults,
	fullWidthAnimation,
	handleAnswered,
	questionElementId,
}) => {
	const animate = useDebounce(!!showResults, 0, false);

	const { percentages } = usePercentages(answers.map((a) => a.votes));

	const fieldsetRef = useRef<HTMLFieldSetElement>(null);

	const navigateOnVerticalArrowDown = (event: KeyboardEvent) => {
		const keys = getKeys(event.nativeEvent);
		const target = event.target as HTMLElement;

		if (keys.isDown) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			getNextFocusableElement(target, fieldsetRef.current)?.focus();
		}

		if (keys.isUp) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			getPrevFocusableElement(target, fieldsetRef.current)?.focus();
		}
	};

	const onKeyDown = (e: KeyboardEvent, focusedAnswerIndex?: number) => {
		navigateOnVerticalArrowDown(e);

		if (typeof focusedAnswerIndex === 'number') {
			const keys = getKeys(e);

			if (keys.isEnter || keys.isSpace) {
				handleAnswered(focusedAnswerIndex);
			}
		}
	};

	return (
		<fieldset
			className="poll-answers"
			aria-labelledby={questionElementId}
			ref={fieldsetRef}
		>
			{answers.map((a, i) => (
				<div
					key={'answer-' + a.answer}
					className={joinString([
						'poll-answer',
						fullWidthAnimation ? 'full-width-animation' : '',
						showResults ? 'result' : '',
						animate ? 'animate' : '',
					])}
				>
					{showResults ? (
						<PollAnswer
							{...a}
							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore
							answered={answered && i === parseInt(answered)}
							percentage={percentages[i]}
							onKeyDown={onKeyDown}
						/>
					) : (
						<div
							role="radio"
							aria-checked="false"
							tabIndex={0}
							className="answer"
							onClick={() => handleAnswered(i)}
							data-link-event={a.dataLinkEvent}
							onKeyDown={(e) => onKeyDown(e, i)}
						>
							{a.answer}
						</div>
					)}
				</div>
			))}
		</fieldset>
	);
};
